import Vue from 'vue'
import Router from 'vue-router'
import store from './store'
import VueResource from 'vue-resource'
import { isTokenExpired } from '@/utils/jwt'
import { canOpenRoute } from '@/services/permissions'

Vue.use(Router)
Vue.use(VueResource)

let routes = [
  {
    path: '/',
    component: () => import('./views/Dashboard/dashboard.vue'),
    name: 'Dashboard',
    meta: {
      showBackBtn: false, public: false, breadcrumb: 'Dashboard' }
  },
  {
    path: '/Users/Settings',
    component: () => import('./views/Users/userSettings.vue'),
    name: 'User Settings',
    meta: { showBackBtn: false, public: false, breadcrumb: 'User Settings' }
  },
  {
    path: '/CanceledLines',
    component: () => import('./views/CanceledLines/canceledLinesList.vue'),
    name: 'Canceled Lines',
    meta: { rule: 'canceledlines', showBackBtn: false, public: false, breadcrumb: 'Canceled Lines' },
    children: [
      { path: '/CanceledLine/:id', component: () => import('./views/CanceledLines/canceledLineDetails.vue'), name: 'Canceled Line', meta: { showBackBtn: true, public: false, breadcrumb: 'Canceled Line' } }
    ]
  },
  {
    path: '/Problems',
    component: () => import('./views/Problems/problemsList.vue'),
    name: 'Problems',
    meta: { rule: 'problems', showBackBtn: false, public: false, breadcrumb: 'Problems' },
    children: [
      { path: '/Problem', component: () => import('./views/Problems/problemAdd.vue'), name: 'Creating problem', meta: { showBackBtn: false, public: false, breadcrumb: 'Creating problem' } },
      { path: '/Problem/:id', component: () => import('./views/Problems/problemDetails.vue'), name: 'Problem', meta: { showBackBtn: false, public: false, breadcrumb: 'Problem' } }
    ]
  },
  {
    path: '/Delays',
    component: () => import('./views/Delays/delayList.vue'),
    name: 'Delays',
    meta: { rule: 'delays', showBackBtn: false, public: false, breadcrumb: 'Delays' },
    children: [
      { path: '/Delay/:id', component: () => import('./views/Delays/delayDetails.vue'), name: 'Delay', meta: { showBackBtn: false, public: false, breadcrumb: 'Delay' } }
    ]
  },
  {
    path: '/Returns',
    component: () => import('./views/Returns/returnList.vue'),
    name: 'Returns',
    meta: { rule: 'returns', showBackBtn: false, public: false, breadcrumb: 'Returns' },
    children: [
      { path: '/Return', component: () => import('./views/Returns/returnAdd.vue'), name: 'Creating return', meta: { showBackBtn: false, public: false, breadcrumb: 'Creating return' } },
      { path: '/Return/:id', component: () => import('./views/Returns/returnDetails.vue'), name: 'Return', meta: { showBackBtn: false, public: false, breadcrumb: 'Return' } }
    ]
  },
  {
    path: '/InboundActs',
    component: () => import('./views/InboundActs/inboundActList.vue'),
    name: 'Inbound Acts',
    meta: { rule: 'inboundacts', showBackBtn: false, public: false, breadcrumb: 'Inbound Acts' },
    children: [
      { path: '/InboundAct', component: () => import('./views/InboundActs/inboundActAdd.vue'), name: 'Creating Inbound Act', meta: { showBackBtn: false, public: false, breadcrumb: 'Creating Inbound Act' } },
      { path: '/InboundAct/:id', component: () => import('./views/InboundActs/inboundActDetails.vue'), name: 'Inbound Act', meta: { showBackBtn: false, public: false, breadcrumb: 'Inbound Act' } }
    ]
  },
  {
    path: '/Report/Problems',
    component: () => import('./views/Reports/Problems/index.vue'),
    name: 'Problem Products Report',
    meta: { rule: 'reports:problems', showBackBtn: false, public: false, breadcrumb: 'Problems' }
  },
  {
    path: '/Report/ProblemsByDepartments',
    component: () => import('./views/Reports/ProblemsByDepartments/problemsByDepartmentsList.vue'),
    name: 'Problems By Departments Report',
    meta: { rule: 'reports:problemsbydepartments', showBackBtn: false, public: false, breadcrumb: 'Problems By Departments' }
  },
  {
    path: '/Claims',
    component: () => import('./views/Claims/Index.vue'),
    name: 'Claims',
    meta: { showBackBtn: false, public: true, breadcrumb: 'Claims' }
  },
  {
    path: '/login',
    component: () => import('./views/Auth/login.vue'),
    name: 'Login',
    meta: { showBackBtn: false, public: true }
  },
  {
    path: '/forgotPassword',
    component: () => import('./views/Auth/forgotPassword.vue'),
    name: 'Forgot Password',
    meta: { showBackBtn: false, public: true }
  },
  {
    path: '/resetPassword',
    component: () => import('./views/Auth/resetPassword.vue'),
    name: 'Reset Password',
    meta: { showBackBtn: false, public: true }
  },
  {
    path: '/noAccess',
    component: () => import('./views/Errors/noAccess.vue'),
    name: 'No Access',
    meta: { showBackBtn: false, public: false, breadcrumb: 'No access' }
  },
  {
    path: '*',
    component: () => import('./views/Shared/404.vue'),
    meta: { showBackBtn: false, public: false, breadcrumb: 'Error' }
  }
]

let router = new Router({
  mode: 'history',
  linkActiveClass: 'active',
  linkExactActiveClass: 'active',
  routes: routes,
  base: process.env.BASE_URL
})

/**
 * Whether the permission list, as it stands right now, says no to this route.
 *
 * An empty list is not an answer - it is what the store holds before the request
 * comes back - so it reads as yes here and is settled by the watcher below.
 */
const refuses = route => {
  const permissions = store.getters.getPermissions
  return permissions.length > 0 && !canOpenRoute(route.matched, permissions)
}

const refuseIfNotAllowed = route => {
  if (route && route.matched.some(record => !record.meta.public) && refuses(route)) {
    router.replace({ name: 'No Access', query: { from: route.fullPath } })
  }
}

/**
 * Deciding this inside beforeEach would mean holding the first navigation until the
 * permission request answered, and nothing renders until a route resolves - not the
 * layout, not the sidebar, and not the jQuery widgets that bind to them on window
 * load. So the first page opens on whatever the list says at the time, and the
 * answer is revisited once it is really known.
 *
 * Which of the two arrives first is a race - the list is fetched as the app starts,
 * while the route resolves an async component - so both are checked and whichever
 * lands last is the one that decides.
 */
store.watch(() => store.getters.getPermissions, () => refuseIfNotAllowed(router.currentRoute))
router.afterEach(to => refuseIfNotAllowed(to))

router.beforeEach((to, from, next) => {
  if (to.matched.some(record => !record.meta.public)) {
    const token = localStorage.getItem('token')
    const loggedIn = store.getters.isLoggedIn && token && !isTokenExpired(token)

    if (loggedIn) {
      if (from.name === 'Login') {
        window.location.href = from.query.redirect || '/'
      }
      else if (refuses(to)) {
        next({ name: 'No Access', query: { from: to.fullPath }, replace: true })
      }
      else {
        next()
      }
    }
    else {
      // Token absent or expired — clear it so stale state doesn't leak into
      // the next session, then send the user to login.
      if (token) localStorage.removeItem('token')
      next({
        path: '/login',
        query: { redirect: to.fullPath }
      })
    }
  }
  else {
    next()
  }
})

export default router
